import React from 'react'
import { Button, Card, Form, Input, Modal, Popconfirm, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd'
import { listDeliveryLocations, createDeliveryLocation, updateDeliveryLocation, deleteDeliveryLocation } from '../../api/deliveryLocations'

export default function DeliveryLocationsAdmin() {
  const [data, setData] = React.useState([])
  const [loading, setLoading] = React.useState(false)

  const [createOpen, setCreateOpen] = React.useState(false)
  const [creating, setCreating] = React.useState(false)
  const [editOpen, setEditOpen] = React.useState(false)
  const [editing, setEditing] = React.useState(false)
  const [selected, setSelected] = React.useState(null)
  const [togglingId, setTogglingId] = React.useState(null)

  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const fetchData = React.useCallback(async () => {
    setLoading(true)
    try {
      const items = await listDeliveryLocations()
      setData(items || [])
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { fetchData() }, [fetchData])

  function parseError(e) {
    const t = (e && e.message) ? e.message : 'Request failed'
    return t.replace(/^\s*"?|"?\s*$/g, '')
  }

  async function onCreate(values) {
    setCreating(true)
    try {
      const payload = {
        nameAr: values.nameAr?.trim() || undefined,
        isCustomerVisible: values.isCustomerVisible !== false,
      }
      await createDeliveryLocation(payload)
      message.success('تم الإنشاء')
      setCreateOpen(false)
      createForm.resetFields()
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setCreating(false)
    }
  }

  function openEdit(record) {
    setSelected(record)
    editForm.setFieldsValue({
      nameAr: record.nameAr,
      isCustomerVisible: record.isCustomerVisible !== false,
    })
    setEditOpen(true)
  }

  async function onEdit(values) {
    if (!selected) return
    setEditing(true)
    try {
      const payload = {}
      if (values.nameAr !== selected.nameAr) payload.nameAr = values.nameAr?.trim()
      const wantVisible = values.isCustomerVisible !== false
      if (wantVisible !== (selected.isCustomerVisible !== false)) {
        payload.isCustomerVisible = wantVisible
      }

      if (Object.keys(payload).length === 0) {
        message.info('لا تغييرات للحفظ')
      } else {
        await updateDeliveryLocation(selected.id, payload)
        message.success('تم التحديث')
      }
      setEditOpen(false)
      setSelected(null)
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setEditing(false)
    }
  }

  async function onDelete(id) {
    try {
      await deleteDeliveryLocation(id)
      message.success('تم الحذف')
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    }
  }

  async function toggleVisible(record, nextValue) {
    setTogglingId(record.id)
    try {
      await updateDeliveryLocation(record.id, { isCustomerVisible: nextValue })
      message.success(nextValue ? 'أصبحت ظاهرة للزبائن' : 'أصبحت مخفية عن الزبائن')
      await fetchData()
    } catch (e) {
      console.error(e)
      message.error(parseError(e))
    } finally {
      setTogglingId(null)
    }
  }

  const columns = [
    { title: 'رقم', dataIndex: 'id', key: 'id', width: 90 },
    {
      title: 'الإسم',
      key: 'nameAr',
      render: (_, r) => (
        <Space size={4}>
          <span>{r.nameAr}</span>
          {r.isCustomerVisible === false && <Tag color="orange">مخفية</Tag>}
        </Space>
      ),
    },
    {
      title: (
        <Tooltip title="عند إيقاف هذا الخيار لن يستطيع الزبائن اختيار هذه المنطقة في التطبيق، لكنها تبقى متاحة للطلبات الخارجية والإدارة">
          ظاهرة للزبائن
        </Tooltip>
      ),
      key: 'isCustomerVisible',
      width: 160,
      render: (_, r) => (
        <Switch
          checked={r.isCustomerVisible !== false}
          loading={togglingId === r.id}
          onChange={v => toggleVisible(r, v)}
        />
      ),
    },
    {
      title: '', key: 'actions', width: 220,
      render: (_, record) => (
        <Space>
          <Button onClick={() => openEdit(record)}>تعديل</Button>
          <Popconfirm title="Delete this location?" onConfirm={() => onDelete(record.id)}>
            <Button danger>حذف</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{maxWidth: 900, margin: '40px auto', padding: '0 16px'}}>
      <Space style={{display: 'flex', justifyContent: 'space-between', marginBottom: 16}}>
        <Typography.Title level={3} style={{margin: 0}}>مناطق التوصيل</Typography.Title>
        <Button type="primary" onClick={() => setCreateOpen(true)}>إضافة منطقة</Button>
      </Space>
      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="إضافة منطقة"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        okText="إضافة"
        confirmLoading={creating}
        onOk={() => createForm.submit()}
      >
        <Form form={createForm} layout="vertical" onFinish={onCreate} initialValues={{ isCustomerVisible: true }}>
          <Form.Item label="الإسم" name="nameAr" rules={[{ required: true, message: 'يرجى إدخال الاسم' }]} >
            <Input />
          </Form.Item>
          <Form.Item
            label="ظاهرة للزبائن"
            name="isCustomerVisible"
            valuePropName="checked"
            extra="عند الإيقاف، تكون المنطقة متاحة للطلبات الخارجية فقط ولا تظهر في قائمة الزبائن."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="تعديل المنطقة"
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        okText="حفظ"
        confirmLoading={editing}
        onOk={() => editForm.submit()}
      >
        <Form form={editForm} layout="vertical" onFinish={onEdit}>
          <Form.Item label="الإسم" name="nameAr">
            <Input />
          </Form.Item>
          <Form.Item label="ظاهرة للزبائن" name="isCustomerVisible" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
